import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { WholesaleOrderListViewService } from '@jfw/angular/wholesale/order-list-view';
import { WholesalePotentialEventService } from '@jfw/angular/wholesale/potential-events-list-view';
import {
  DashboardPod,
  EventListItem,
  EventListItemWrapper,
  EzTuxEnvironment,
  EzTuxParentApplication,
  OrderListItem,
  OrderListItemWrapper,
  PodEventEmission,
} from '@jfw/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { WholesaleDataPodComponent } from './data-pod/data-pod.component';
import {
  WholesaleDataPodsService,
  WholesaleDataPodType,
} from './data-pods.service';

// The weekly order pods sort by Order # by default (EXT-6037, EXT-6430);
// every other order pod sorts by event date.
export function isWeekPod(
  dashboardPod: DashboardPod | null | undefined,
): boolean {
  return (
    dashboardPod?.pod === 'THIS_WEEK' ||
    dashboardPod?.pod === 'NEXT_WEEK' ||
    dashboardPod?.pod === 'LAST_WEEK'
  );
}

@Component({
  selector: 'jfw-wholesale-data-pods',
  imports: [WholesaleDataPodComponent, MatIconModule, MatButtonModule],
  templateUrl: './data-pods.component.html',
  styleUrl: './data-pods.component.scss',
})
export class WholesaleDataPodsComponent implements OnInit {
  @ViewChildren(WholesaleDataPodComponent)
  podComponents!: QueryList<WholesaleDataPodComponent>;

  podsService = inject(WholesaleDataPodsService);
  private readonly destroyRef = inject(DestroyRef);
  private latestFetchId = 0;
  _platform_order_base_url: string = '';

  dashboardTableClassOverride = '';

  // podsLastUpdated: number = 0;
  podsLastUpdated = computed(() => this.podsService.podsLastUpdated());

  @Input() set platform_order_base_url(value: string) {
    this._platform_order_base_url = value;
    this.podsService.setPlatformOrderBaseUrl(value);
  }
  get platform_order_base_url() {
    return this._platform_order_base_url;
  }
  @Input() actnum: string | null = null;

  @Input()
  set tablestyle(value: string) {
    console.log('Setting dashboard table style:', value);
    this.dashboardTableClassOverride = value;
  }
  get tablestyle(): string {
    return this.dashboardTableClassOverride;
  }

  @Input() parent_application: EzTuxParentApplication;
  @Input() environment: EzTuxEnvironment;
  @Input() newSite = false;
  // EXT-6430: opt-in Orders Last Week pod. Platform Portal and Dealer Portal
  // turn it on; the KMS/EZTux embed never binds it, so it stays off there.
  @Input() showOrdersLastWeek = false;

  @Output() newDashboardPodEvent = new EventEmitter<DashboardPod | null>();

  thisWeek: DashboardPod | null = null;
  nextWeek: DashboardPod | null = null;
  lastWeek: DashboardPod | null = null;
  lateReturn: DashboardPod | null = null;
  missingSize: DashboardPod | null = null;
  possibleDuplicates: DashboardPod | null = null;
  potentialEvents: DashboardPod | null = null;
  podCurrentlyExpanded: string | undefined;

  initialized: boolean = false;
  forceNextFetch: boolean = false;

  isEztBo: boolean = this.getIsEztBo();
  isEzt: boolean = this.getIsEzt();

  constructor(
    private orderListViewService: WholesaleOrderListViewService,
    private potentialEventService: WholesalePotentialEventService,
  ) {}

  ngOnInit(): void {
    // this.fetchData();
    // console.log('Pods parent_application:', this.parent_application);
    // console.log('Pods environment:', this.environment);
    // EXT-6431: the service is a root singleton, so without
    // takeUntilDestroyed every visit to the page added a subscriber, and one
    // Reject reloaded the pods once per visit.
    this.potentialEventService.fetchDataTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fetchData(true);
      });

    /// Check if refreshDataPods cookie exists and trigger fetchData if so
    const refreshDataPods = this.getCookie('refreshDataPods');

    // console.log('RefreshData from cookie?', refreshDataPods);

    if (refreshDataPods !== undefined) {
      // console.log('Should Refresh from Cookie');
      this.deleteCookie('refreshDataPods');
      this.forceNextFetch = true;
    }

    this.fetchData();
    this.initialized = true;
  }

  prettifyEpoch(epochSeconds: number): string {
    if (epochSeconds === 0) {
      return 'unknown';
    }

    const date = new Date(epochSeconds * 1000);
    return (
      'Last Queried: ' +
      date.toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    );
  }

  getIsEztBo() {
    const hostname = window.location.host;

    const subdomainPattern =
      /^(ezdevbo-1|ezdevbo-2025|eztestbo-1|eztestbo-2025|eztrainbo-2025|dr-eztuxbo-2025|eztuxbo-2025|eztuxbo)\.jimsfw\.com$/i;

    if (subdomainPattern.test(hostname)) {
      return true;
    }

    return false;
  }

  getIsEzt() {
    const hostname = window.location.host;

    const subdomainPattern =
      /^(ezdev-1|ezdev-2025|eztest-1|eztest-2025|eztrain-2025|dr-eztux-2025|eztux-2025|eztux)\.jimsfw\.com$/i;

    if (subdomainPattern.test(hostname)) {
      return true;
    }

    return false;
  }

  getPrettyDate(epochSeconds: number): string {
    if (epochSeconds === 0) {
      return 'unknown';
    }

    const date = new Date(epochSeconds * 1000);
    const datePart = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  }

  isValid(): boolean {
    return !!this.actnum;
  }

  // Allowlist (default-deny), matching the pattern already established by
  // WholesaleOrderListViewComponent.redirectToReceipt() — an unrecognized or
  // unset parent_application (including the KMS/eztux embed) always falls
  // through to the legacy design.
  get showNewDesign(): boolean {
    return (
      this.parent_application === 'jfwplatformportal' ||
      (this.parent_application === 'dealerportal' && this.newSite)
    );
  }

  themeFor(base: string): string {
    return this.showNewDesign ? `${base} newsite` : base;
  }

  // Refresh Data control visibility is dealer-portal-only: jfw-platform-portal
  // already renders its own separate refresh button/timestamp around this
  // same component (see its standalone/components/wholesale/data-pods
  // wrapper), so showing this one too would duplicate it.
  get showRefreshControl(): boolean {
    return (
      this.isEzt ||
      this.isEztBo ||
      (this.parent_application === 'dealerportal' && this.newSite)
    );
  }

  // Asp.Net webforms loads the control before attributes are set, so
  // we need to check if the attributes are set before calling fetchData.
  ngOnChanges(changes: SimpleChanges) {
    // console.log('ngOnChanges called with changes:', changes);
    if (
      this.initialized &&
      (changes['platform_order_base_url'] ||
        changes['actnum'] ||
        changes['showOrdersLastWeek'])
    ) {
      console.log('ngOnChanges platform_order_base_url or actnum changed');

      this.resetPods();
      this.fetchData();
    }
  }

  setPodData(podId: string, response: DashboardPod | null): void {
    const podComponent = this.getPodComponentByPodId(podId);
    if (!podComponent || !response) {
      return;
    }
    if (response.responseStatus === 500) {
      podComponent.contenttext = '!';
      podComponent.podtitle = response.errorMessage.message;
    } else {
      // podComponent.contenttext = response?.itemCount.toString();
      switch (podId) {
        case 'this-week':
          this.thisWeek = response;
          break;
        case 'next-week':
          this.nextWeek = response;
          break;
        case 'last-week':
          this.lastWeek = response;
          break;
        case 'late-return':
          this.lateReturn = response;
          break;
        case 'missing-size':
          this.missingSize = response;
          break;
        case 'duplicate-orders':
          this.possibleDuplicates = response;
          break;
        case 'potential-events':
          this.potentialEvents = response;
          break;
        default:
          console.warn(`Unknown podId: ${podId}`);
      }
    }
  }

  resetPods() {
    if (!this.podComponents || this.podComponents.length === 0) {
      console.warn('No pod components found to reset.');
      return;
    }

    this.podComponents.forEach((pod) => {
      pod.enablespinner = true; // Disable spinner for all pods
      pod.contenttext = undefined; // Reset content text for all pods
      pod.active = false; // Collapse all pods
    });

    this.podCurrentlyExpanded = undefined; // Reset the currently expanded pod

    // Reset eventList
    this.potentialEventService.setEventListWrapper({
      eventList: undefined,
      status: 'PENDING',
    });

    // Reset orderList
    this.orderListViewService.setOrderListWrapper({
      orderList: undefined,
      status: 'PENDING',
    });
  }

  fetchData(forceLoad: boolean = false): void {
    this.podsService.podsLastUpdated.set(0);
    if (!this.isValid()) {
      console.error('an attribute is not set');
      return;
    } else {
      // this.resetPods();

      if (this.forceNextFetch) {
        forceLoad = true;
      }

      this.forceNextFetch = false; // Reset after first use

      console.log('fetching data... with force:', forceLoad);

      this.resetPods();

      this.thisWeek = null;
      this.nextWeek = null;
      this.lastWeek = null;
      this.lateReturn = null;
      this.missingSize = null;
      this.possibleDuplicates = null;
      this.potentialEvents = null;

      // EXT-6431: a response is applied only if it belongs to the latest fetch
      // for the current account, so a slow response can't overwrite a newer
      // fetch or another account's pods.
      const fetchId = ++this.latestFetchId;
      const fetchedFor = this.actnum;
      const isCurrent = () =>
        fetchId === this.latestFetchId && fetchedFor === this.actnum;

      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.THIS_WEEK,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        this.setPodData('this-week', response);
      });
      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.NEXT_WEEK,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        this.setPodData('next-week', response);
      });
      if (this.showOrdersLastWeek) {
        this.fetchDataForPod(
          this.actnum,
          WholesaleDataPodType.LAST_WEEK,
          forceLoad,
        ).then((response) => {
          if (!isCurrent()) return;
          this.setPodData('last-week', response);
        });
      }
      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.LATE_RETURN,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        // this.lateReturn = response;
        this.setPodData('late-return', response);
      });
      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.MISSING_SIZES,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        // this.missingSize = response;
        this.setPodData('missing-size', response);
      });
      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.DUPLICATE_ORDERS,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        // this.possibleDuplicates = response;
        this.setPodData('duplicate-orders', response);
      });
      this.fetchDataForPod(
        this.actnum,
        WholesaleDataPodType.POTENTIAL_EVENTS,
        forceLoad,
      ).then((response) => {
        if (!isCurrent()) return;
        //this.potentialEvents = response;
        this.setPodData('potential-events', response);
        // console.log('Last updated response:', response);
        if (response?.responseStatus === 200) {
          this.podsService.podsLastUpdated.set(response?.lastUpdated || 0);
        }
      });
    }
  }

  sendToOrderGrid(dashboardPod: DashboardPod | null) {
    // Send to output event emitter.
    // console.log('Sending to order grid:', dashboardPod);

    // Only assign OrderListItem[] to orderList, otherwise undefined
    const orderList =
      dashboardPod?.itemList &&
      dashboardPod.itemList.length > 0 &&
      'receiptInternalOrderNumber' in dashboardPod.itemList[0]
        ? (dashboardPod.itemList as OrderListItem[])
        : undefined;

    const newOrderListWrapper: OrderListItemWrapper = {
      orderList,
      status: 'READY',
    };

    if (!dashboardPod) {
      newOrderListWrapper.orderList = undefined;
      newOrderListWrapper.status = 'PENDING';
    }

    // EXT-6188: record the default sort this pod's list should land on, keyed
    // to that list's own array identity. This is the single authoritative copy
    // of the pod -> sort mapping; jfw-platform-portal and dealer-portal each
    // compute the same thing locally to bind [defaultSortKey] (EXT-6037), and
    // the KMS/EZTux embed -- which can't bind that input at all, since the
    // pods and the order list are separate custom elements joined by KMS-side
    // script we don't own -- picks it up from here instead.
    if (orderList) {
      this.orderListViewService.setDefaultSortKeyForList(
        orderList,
        isWeekPod(dashboardPod) ? 'orderNumber' : 'eventDate',
      );
    }

    this.orderListViewService.setOrderListWrapper(newOrderListWrapper);

    // EXT-5994: always emit, including null on collapse/clear -- this was
    // previously gated on `if (dashboardPod)`, so consumers' own
    // onNewItemListEvent(null) clear-out never actually fired. That left
    // jfw-platform-portal's/dealer-portal's own local orderList/eventList as
    // dead state, so both pages fell back entirely on the shared
    // WholesaleOrderListViewService/WholesalePotentialEventService singletons
    // to decide what to render -- including on a fresh page mount, where
    // those singletons can still hold a previous pod selection's data.
    this.newDashboardPodEvent.emit(dashboardPod);
  }

  sendToEventGrid(dashboardPod: DashboardPod | null) {
    // Send to output event emitter.
    console.log('Sending to event grid:', dashboardPod);

    // Only assign OrderListItem[] to orderList, otherwise undefined
    const eventList =
      dashboardPod?.itemList &&
      dashboardPod.itemList.length > 0 &&
      'omniId' in dashboardPod.itemList[0]
        ? (dashboardPod.itemList as EventListItem[])
        : undefined;

    const newEventListWrapper: EventListItemWrapper = {
      eventList,
      status: 'READY',
    };

    if (!dashboardPod) {
      newEventListWrapper.eventList = undefined;
      newEventListWrapper.status = 'PENDING';
    }

    this.potentialEventService.setEventListWrapper(newEventListWrapper);

    // EXT-5994: always emit -- see sendToOrderGrid()'s comment above, same
    // reasoning applies here.
    this.newDashboardPodEvent.emit(dashboardPod);
  }

  async fetchDataForPod(
    accountNumber: string | null,
    eztPodKind: WholesaleDataPodType,
    forceLoad: boolean,
  ): Promise<DashboardPod | null> {
    try {
      const response = await firstValueFrom(
        this.podsService.getPodData(
          this.parent_application,
          accountNumber,
          eztPodKind,
          forceLoad,
        ),
      );
      // console.log('Pod data fetched:', response);
      return response;
    } catch (err) {
      console.error('Error fetching data for pod:', err);
      const errorPod: DashboardPod = {
        pod: eztPodKind,
        itemCount: 0,
        itemList: [],
        accountNumber: accountNumber ?? '',
        responseStatus: 500,
        lastUpdated: Date.now(),
        errorMessage: err,
      };
      return errorPod;
    }
  }

  setActivePod(podId: string, podData: DashboardPod | null) {
    const pod = this.getPodComponentByPodId(podId) as WholesaleDataPodComponent;

    this.sendToEventGrid(null); // Clear the event grid
    this.sendToOrderGrid(null); // Clear the order grid

    if (this.podCurrentlyExpanded === pod.id) {
      this.podCurrentlyExpanded = undefined; // Collapse the pod
      if (podId === 'potential-events') {
        this.sendToEventGrid(null); // Clear the event grid
      } else {
        this.sendToOrderGrid(null); // Clear the order grid
      }

      pod.active = false;
    } else {
      this.podCurrentlyExpanded = pod.id; // Expand the pod

      if (podId === 'potential-events') {
        this.sendToEventGrid(podData);
      } else {
        this.sendToOrderGrid(podData); // Send the pod data to the order grid
      }
      pod.active = true;
    }

    // Set the active state of the other pods to false
    this.podComponents.forEach((p) => {
      if (p.id !== podId) {
        p.active = false; // Collapse other pods
      }
    });
  }

  handlePodClick(podEventEmission: PodEventEmission) {
    console.log('Pod clicked! Emitting event...');
    console.log(podEventEmission);

    switch (podEventEmission.podId) {
      case 'this-week':
        const pod = this.getPodComponentByPodId(
          'this-week',
        ) as WholesaleDataPodComponent;
        // if (this.podCurrentlyExpanded === 'this-week') {
        //   this.podCurrentlyExpanded = undefined; // Collapse the pod
        //   this.sendToOrderGrid(null); // Clear the order grid
        //   pod.showbottomarrow = true;
        // } else {
        //   this.podCurrentlyExpanded = 'this-week'; // Expand the pod
        //   this.sendToOrderGrid(this.thisWeek);
        //   pod.showbottomarrow = false;
        // }
        this.setActivePod('this-week', this.thisWeek);
        break;
      case 'next-week':
        // if (this.podCurrentlyExpanded === 'next-week') {
        //   this.podCurrentlyExpanded = undefined; // Collapse the pod
        //   this.sendToOrderGrid(null); // Clear the order grid
        // } else {
        //   this.podCurrentlyExpanded = 'next-week'; // Expand the pod
        //   this.sendToOrderGrid(this.nextWeek);
        // }
        this.setActivePod('next-week', this.nextWeek);
        break;
      case 'last-week':
        this.setActivePod('last-week', this.lastWeek);
        break;
      case 'late-return':
        // this.sendToOrderGrid(this.lateReturn);
        this.setActivePod('late-return', this.lateReturn);
        break;
      case 'missing-size':
        // this.sendToOrderGrid(this.missingSize);
        this.setActivePod('missing-size', this.missingSize);
        break;
      case 'duplicate-orders':
        // this.sendToOrderGrid(this.possibleDuplicates);
        this.setActivePod('duplicate-orders', this.possibleDuplicates);
        break;
      case 'potential-events':
        // this.sendToOrderGrid(this.potentialEvents);
        this.setActivePod('potential-events', this.potentialEvents);
        break;
      default:
        console.error('Unknown pod ID:', podEventEmission.podId);
        break;
    }

    // // Example of updating the data of another pod based on the clicked pod
    // if (podEventEmission.podId === 'next-week') {
    //   this.updatePodData('this-week', {
    //     newData: 'New data from ' + podEventEmission.podId,
    //   });
    // }
  }

  getPodHoverEnter(podEventEmission: PodEventEmission) {
    console.log('Pod hovered - enter! Emitting event...');
    console.log(podEventEmission);
  }

  getPodHoverLeave(podEventEmission: PodEventEmission) {
    console.log('Pod hovered - leave! Emitting event...');
    console.log(podEventEmission);
  }

  getPodComponentByPodId(podId: string): WholesaleDataPodComponent | null {
    if (!this.podComponents || this.podComponents.length === 0) {
      console.warn('No pod components found.');
      return null;
    }
    const pod = this.podComponents.find((component) => component.id === podId);
    return pod || null;
  }

  updatePodData(podId: string, newData: any): void {
    const pod = this.getPodComponentByPodId(podId);
    if (pod) {
      pod.data = newData; // Update the data property
      console.log(`Updated data for pod with id ${podId}:`, newData);
    } else {
      console.error(`Pod with id ${podId} not found.`);
    }
  }

  deleteCookie(name: string) {
    document.cookie = name + '=; Max-Age=0; path=/';
    // console.log(`Deleted cookie: ${name}`);
  }

  getCookie(name: string) {
    let ca: Array<string> = document.cookie.split(';');
    let caLen: number = ca.length;
    let cookieName = `${name}=`;
    let c: string;

    for (let i: number = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) == 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return undefined;
  }
}
